// Sophia — real-time satellite tracking.
// TLE sets are fetched from CelesTrak when a network is available and cached
// offline; propagation is a lightweight circular-orbit (mean-motion) model that
// is accurate enough for live ground tracks, orbit traces and pass prediction
// without pulling a full SGP4 dependency.

import { getOption, setOption } from "./persistOptions";
// Cross-origin GETs leave through Sophia's own same-origin relay: browsers
// block these hosts on CORS, so a direct fetch is a guaranteed failure.
import { relayFetch } from "./privacyNet";

export interface Tle { name: string; l1: string; l2: string; }

export interface OrbitElements {
  name: string;
  inclinationDeg: number;
  raanDeg: number;
  eccentricity: number;
  argPerigeeDeg: number;
  meanAnomalyDeg: number;
  meanMotionRevPerDay: number;
  epoch: number;           // ms
  altitudeKm: number;
  periodMin: number;
  catalog: string;
}

export interface SatPosition {
  name: string;
  lat: number;
  lng: number;
  altitudeKm: number;
  velocityKms: number;
  periodMin: number;
  inclinationDeg: number;
}

const CACHE_KEY = "sat:tle:cache";
const GROUPS = ["stations", "visual", "weather", "science", "gps-ops", "starlink", "military", "geo"] as const;
export type SatGroup = (typeof GROUPS)[number];
export const SAT_GROUPS: SatGroup[] = [...GROUPS];

/** Baked-in fallback so tracking works with zero network. */
const FALLBACK: Tle[] = [
  {
    name: "ISS (ZARYA)",
    l1: "1 25544U 98067A   24180.50000000  .00016717  00000+0  30074-3 0  9995",
    l2: "2 25544  51.6416 247.4627 0006703 130.5360 325.0288 15.50377579 47398",
  },
  {
    name: "CSS (TIANHE)",
    l1: "1 48274U 21035A   24180.50000000  .00021000  00000+0  24000-3 0  9990",
    l2: "2 48274  41.4700 120.0000 0005000  90.0000 270.0000 15.60000000 20000",
  },
  {
    name: "HUBBLE SPACE TELESCOPE",
    l1: "1 20580U 90037B   24180.50000000  .00000900  00000+0  49000-4 0  9996",
    l2: "2 20580  28.4690 210.0000 0002500 210.0000 150.0000 15.09000000 60000",
  },
  {
    name: "NOAA 19",
    l1: "1 33591U 09005A   24180.50000000 -.00000030  00000+0  25000-4 0  9993",
    l2: "2 33591  99.1900 220.0000 0013500 300.0000  60.0000 14.12500000 79000",
  },
  {
    name: "TERRA",
    l1: "1 25994U 99068A   24180.50000000  .00000200  00000+0  60000-4 0  9991",
    l2: "2 25994  98.2100 250.0000 0001200 100.0000 260.0000 14.57100000 28000",
  },
  {
    name: "AQUA",
    l1: "1 27424U 02022A   24180.50000000  .00000250  00000+0  65000-4 0  9994",
    l2: "2 27424  98.2000 260.0000 0001500 110.0000 250.0000 14.57200000 18000",
  },
  {
    name: "SENTINEL-1A",
    l1: "1 39634U 14016A   24180.50000000  .00000100  00000+0  30000-4 0  9992",
    l2: "2 39634  98.1800 180.0000 0001300 120.0000 240.0000 14.59200000 54000",
  },
  {
    name: "GPS BIIF-2 (PRN 01)",
    l1: "1 37753U 11036A   24180.50000000 -.00000090  00000+0  00000+0 0  9997",
    l2: "2 37753  55.4000 150.0000 0080000 200.0000 160.0000  2.00560000 90000",
  },
];

export function cachedTles(): Tle[] {
  const c = getOption<Tle[]>(CACHE_KEY, []);
  return c.length ? c : FALLBACK;
}

/**
 * Live orbital elements. CelesTrak now refuses requests that do not come from a
 * browser, so she asks it first and then falls back to open element sets that
 * still answer, before finally using the stored set.
 */
function tleSources(group: SatGroup): string[] {
  return [
    `https://celestrak.org/NORAD/elements/gp.php?GROUP=${group}&FORMAT=tle`,
    `https://cdn.jsdelivr.net/gh/tle-tools/tle-data@main/${group}.txt`,
    "https://live.ariss.org/iss.txt",
    "https://api.wheretheiss.at/v1/satellites/25544/tles?format=text",
  ];
}

export async function refreshTles(group: SatGroup = "stations"): Promise<{ tles: Tle[]; offline: boolean }> {
  for (const src of tleSources(group)) {
    try {
      const r = await relayFetch(src);
      if (!r.ok) continue;
      const tles = parseTleText(await r.text()).slice(0, 120);
      if (tles.length === 0) continue;
      const merged = dedupe([...tles, ...cachedTles()]).slice(0, 300);
      setOption(CACHE_KEY, merged);
      return { tles: merged, offline: false };
    } catch { /* try the next source */ }
  }
  return { tles: cachedTles(), offline: true };
}

function dedupe(list: Tle[]): Tle[] {
  const seen = new Set<string>();
  return list.filter((t) => (seen.has(t.name) ? false : (seen.add(t.name), true)));
}

export function parseTleText(text: string): Tle[] {
  const lines = text.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.length > 0);
  const out: Tle[] = [];
  for (let i = 0; i < lines.length - 2; i++) {
    if (lines[i + 1].startsWith("1 ") && lines[i + 2].startsWith("2 ")) {
      out.push({ name: lines[i].trim(), l1: lines[i + 1], l2: lines[i + 2] });
      i += 2;
    }
  }
  return out;
}

const MU = 398_600.4418;      // km³/s²
const EARTH_R = 6371;         // km

export function elementsFrom(tle: Tle): OrbitElements {
  const num = (l: string, a: number, b: number) => parseFloat(l.slice(a, b).trim()) || 0;
  const epochYear = num(tle.l1, 18, 20);
  const epochDay = num(tle.l1, 20, 32);
  const year = epochYear < 57 ? 2000 + epochYear : 1900 + epochYear;
  const epoch = Date.UTC(year, 0, 1) + (epochDay - 1) * 86_400_000;
  const meanMotion = num(tle.l2, 52, 63);
  const periodMin = meanMotion > 0 ? 1440 / meanMotion : 90;
  const a = Math.cbrt(MU / ((2 * Math.PI) / (periodMin * 60)) ** 2);
  return {
    name: tle.name,
    inclinationDeg: num(tle.l2, 8, 16),
    raanDeg: num(tle.l2, 17, 25),
    eccentricity: num(tle.l2, 26, 33) / 1e7,
    argPerigeeDeg: num(tle.l2, 34, 42),
    meanAnomalyDeg: num(tle.l2, 43, 51),
    meanMotionRevPerDay: meanMotion,
    epoch,
    altitudeKm: a - EARTH_R,
    periodMin,
    catalog: tle.l1.slice(2, 7).trim(),
  };
}

/** Sub-satellite point at a given time (circular-orbit approximation). */
export function propagate(el: OrbitElements, at = Date.now()): SatPosition {
  const minutes = (at - el.epoch) / 60_000;
  const rev = el.meanMotionRevPerDay / 1440;                 // rev per minute
  const M = ((el.meanAnomalyDeg / 360 + rev * minutes) % 1) * 2 * Math.PI;
  const inc = (el.inclinationDeg * Math.PI) / 180;
  const argLat = M + (el.argPerigeeDeg * Math.PI) / 180;

  // Position in the orbital plane -> geodetic-ish lat/lng.
  const lat = Math.asin(Math.sin(inc) * Math.sin(argLat));
  let lonOffset = Math.atan2(Math.cos(inc) * Math.sin(argLat), Math.cos(argLat));

  // RAAN drift (J2 nodal regression) + Earth rotation.
  const aKm = el.altitudeKm + EARTH_R;
  const j2Drift = -9.964 * (6378.137 / aKm) ** 3.5 * Math.cos(inc);   // deg/day
  const raan = el.raanDeg + (j2Drift * minutes) / 1440;
  const gmst = greenwichHourAngle(at);
  let lng = ((raan * Math.PI) / 180 + lonOffset - gmst) * (180 / Math.PI);
  lng = ((lng + 540) % 360) - 180;
  lonOffset = 0;

  const v = Math.sqrt(MU / aKm);
  return {
    name: el.name,
    lat: (lat * 180) / Math.PI,
    lng,
    altitudeKm: el.altitudeKm,
    velocityKms: v,
    periodMin: el.periodMin,
    inclinationDeg: el.inclinationDeg,
  };
}

function greenwichHourAngle(at: number): number {
  const jd = at / 86_400_000 + 2_440_587.5;
  const t = jd - 2_451_545.0;
  const deg = (280.46061837 + 360.98564736629 * t) % 360;
  return ((deg + 360) % 360) * (Math.PI / 180);
}

/** A full orbit ground track, for drawing the orbit line on the map. */
export function groundTrack(el: OrbitElements, at = Date.now(), points = 90): SatPosition[] {
  const step = (el.periodMin * 60_000) / points;
  const out: SatPosition[] = [];
  for (let i = 0; i < points; i++) out.push(propagate(el, at + i * step));
  return out;
}

/** Great-circle distance in km. */
export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** Rough "is it overhead" visibility test from an observer. */
export function isVisibleFrom(pos: SatPosition, obsLat: number, obsLng: number): { visible: boolean; elevationDeg: number; rangeKm: number } {
  const d = distanceKm(obsLat, obsLng, pos.lat, pos.lng);
  const horizon = Math.acos(EARTH_R / (EARTH_R + pos.altitudeKm)) * EARTH_R;
  const frac = Math.min(1, d / Math.max(horizon, 1));
  const elevationDeg = Math.max(0, 90 * (1 - frac));
  return { visible: d <= horizon, elevationDeg, rangeKm: Math.sqrt(d * d + pos.altitudeKm ** 2) };
}

/** Next passes over an observer within the given window. */
export function nextPasses(
  el: OrbitElements,
  obsLat: number,
  obsLng: number,
  windowMin = 180,
  stepMin = 1,
): Array<{ startsAt: number; endsAt: number; peakElevationDeg: number }> {
  const passes: Array<{ startsAt: number; endsAt: number; peakElevationDeg: number }> = [];
  const now = Date.now();
  let open: { startsAt: number; peak: number } | null = null;
  for (let m = 0; m <= windowMin; m += stepMin) {
    const at = now + m * 60_000;
    const v = isVisibleFrom(propagate(el, at), obsLat, obsLng);
    if (v.visible) {
      if (!open) open = { startsAt: at, peak: v.elevationDeg };
      else open.peak = Math.max(open.peak, v.elevationDeg);
    } else if (open) {
      passes.push({ startsAt: open.startsAt, endsAt: at, peakElevationDeg: open.peak });
      open = null;
    }
  }
  if (open) passes.push({ startsAt: open.startsAt, endsAt: now + windowMin * 60_000, peakElevationDeg: open.peak });
  return passes.slice(0, 6);
}
